import React, { Fragment } from "react";
import { Routes, BrowserRouter, Route, useLocation } from "react-router-dom";

import * as Material from "@mui/material";

import { Confirm } from "@fragments";
import UserModal from "@components/Modals/UserModal";
import ShareContentModal from "@components/Modals/ShareContentModal/index.js";
import VerificationModal from "@components/_modals/Auth/VerificationModal";

import { AuthProvider } from "@contexts/AuthContext.js";
import { CommunityProvider } from "contexts/community";

import { ThemeProvider } from "@mui/material/styles";

import theme from "./assets/theme.js";

import H from "@helpers/H.js";

import { ScreenProvider, useScreen as useLegacyScreen } from "@hooks/useScreen";

import routes from "./routes";
import { isPublicPath } from "@helpers/publicRoutes.js";
import MentoringInvite from "@pages/Mentoring/invite";
import DownloadApp from "@pages/DownloadApp";
import { DownloadAppProvider } from "@features/downloadApp";

import "./App.css";
import "./assets/spacing.css";
import Screen from "Screen/index.js";
import Studio from "@pages/Studio";

function App() {
  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <AuthProvider>
          <CommunityProvider>
            <ScreenProvider>
              <LanguageBoundary>
                <DownloadAppProvider>
                  <AppRoutes />
                </DownloadAppProvider>
              </LanguageBoundary>
            </ScreenProvider>
          </CommunityProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

// Remounts the view tree whenever the UI language changes.
//
// Components that read copy through `useTranslation()` re-render on their own,
// but a good deal of the app renders labels through the plain `t` helper from
// `@i18n` (Parser.payment.chargeStatus, chart tick formatters, enum label maps).
// Those call sites produce the right string for whatever language is active when
// they run, yet nothing subscribes them to a language change, so they would keep
// showing stale text until some unrelated state update happened to re-render
// them. Keying this boundary on the language forces one clean remount instead.
//
// It sits below AuthProvider/CommunityProvider on purpose: the user and their
// communities survive the switch, only the views are rebuilt. Switching language
// is a rare, deliberate action, so discarding transient view state is fine.
function LanguageBoundary({ children }) {
  const { language } = useLegacyScreen();
  return <Fragment key={language}>{children}</Fragment>;
}

// Top-level route switch (§10). Public paths render their own bare <Routes> with
// no app shell (no Sidebar/Header) — the AuthProvider already skips the auth
// redirect for them, so logged-out visitors reach them. Everything else falls
// through to the authenticated, shell-wrapped ProtectedRoutes tree. Only one
// <Routes> is ever mounted at a time, so the protected routes' absolute paths
// are never affected by the public branch.
function AppRoutes() {
  const location = useLocation();

  if (location.pathname === "/studio" || location.pathname.startsWith("/studio/")) {
    return <><SnackbarHost /><Confirm /><Studio /></>;
  }

  if (isPublicPath(location.pathname)) {
    return (
      <>
        <SnackbarHost />
        <Confirm />
        <Routes>
          <Route path="/mentoring/invite/:token" element={<MentoringInvite />} />
          <Route path="/download" element={<DownloadApp />} />
        </Routes>
      </>
    );
  }

  return <ProtectedRoutes />;
}

function ScreenLayers({ children }) {
  return (
    <Screen>
      {children}
    </Screen>
  )
}

function SnackbarHost() {
  const { snackbar, setSnackbar } = useLegacyScreen();
  if (!snackbar) return null;
  return (
    <Material.Portal>
      <Material.Snackbar
        className="custom-snackbar"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={!!snackbar}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        TransitionComponent={Material.Fade}
        TransitionProps={{ timeout: { enter: 0, exit: 200 } }}
        sx={{ zIndex: 9999 }}
      />
    </Material.Portal>
  );
}

const ProtectedRoutes = () => {
  window.helpers = H;

  return (
    <>
      <SnackbarHost />
      {/* Host for sensitive-action verification (428 flows). Sits above the
          route switch so an in-flight flow survives a navigation. */}
      <VerificationModal />
      <ScreenLayers>
        <Routes>
          {Object.keys(routes).map((path, i) => {
            const Component = routes[path];
            return (
              <Route
                path={"/" + path}
                key={i}
                element={
                  <>
                    <Confirm />
                    <UserModal />
                    <Component />
                    <ShareContentModal />
                  </>
                }
              />
            );
          })}
        </Routes>
      </ScreenLayers>
    </>
  );
};

export default App;
